import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic, MicOff, Loader2, BookOpen, Trash2 } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { supabase } from '../../../lib/supabaseClient'
import { useAuth } from '../../../contexts/AuthContext'
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition'
import RewriteButton from '../../../components/RewriteButton'
import { cn } from '../../../lib/utils'

// Voice journal for /journey — a 30-second voice memo (browser STT,
// Egyptian Arabic) becomes a journey_entries row with AI-extracted
// mood/energy/people/topics. The transcript stays editable before
// saving, and typing instead of speaking works the same.

export default function VoiceJournalCard() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const speech = useSpeechRecognition({ lang: locale === 'ar' ? 'ar-EG' : 'en-US' })

  const [text, setText] = useState('')
  const [usedVoice, setUsedVoice] = useState(false)
  const [busy, setBusy] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [entries, setEntries] = useState([])

  useEffect(() => {
    if (!speech.transcript) return
    setUsedVoice(true)
    setText((prev) => (prev ? `${prev} ` : '') + speech.transcript)
    speech.reset()
  }, [speech.transcript])

  const refetch = async () => {
    if (!user) return
    const { data } = await supabase
      .from('journey_entries')
      .select('id, recorded_at, transcript, summary, mood, energy, people, topics')
      .eq('user_id', user.id)
      .order('recorded_at', { ascending: false })
      .limit(5)
    setEntries(data || [])
  }

  useEffect(() => { refetch() }, [user]) // eslint-disable-line

  const save = async () => {
    const transcript = text.trim()
    if (!transcript || busy) return
    setBusy(true)
    setLastSaved(null)
    const { data, error } = await supabase.functions.invoke('journey-voice-entry', {
      body: { transcript, source: usedVoice ? 'voice' : 'text' },
    })
    setBusy(false)
    if (error || !data) { setLastSaved({ failed: true }); return }
    setLastSaved(data)
    setText('')
    setUsedVoice(false)
    refetch()
  }

  const remove = async (id) => {
    await supabase.from('journey_entries').delete().eq('id', id)
    refetch()
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
          <BookOpen className="w-4 h-4 text-app-primary-500" />
          {t('journey.voice.title')}
        </h2>

        <textarea
          value={speech.listening && speech.interim ? `${text} ${speech.interim}`.trim() : text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('journey.voice.placeholder')}
          rows={3}
          maxLength={4000}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
        />

        <div className="flex gap-2">
          {speech.supported && (
            <button
              type="button"
              onClick={() => (speech.listening ? speech.stop() : speech.start())}
              aria-pressed={speech.listening}
              aria-label={t('journey.voice.mic')}
              className={cn(
                'h-10 w-12 rounded-md border inline-flex items-center justify-center flex-shrink-0 transition-colors',
                speech.listening
                  ? 'border-red-500/50 bg-red-500/10 text-red-500 animate-pulse'
                  : 'border-input text-app-primary-500 hover:bg-accent',
              )}
            >
              {speech.listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
          <Button onClick={save} disabled={busy || !text.trim()} className="flex-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('journey.voice.save')}
          </Button>
          <RewriteButton value={text} onRewritten={setText} className="flex-shrink-0" />
        </div>

        {lastSaved?.failed && (
          <p className="text-xs text-red-500" dir="auto">{t('journey.voice.failed')}</p>
        )}
        {lastSaved && !lastSaved.failed && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 space-y-1" dir="auto">
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{t('journey.voice.saved')}</p>
            <EntryChips entry={lastSaved} t={t} />
          </div>
        )}

        {entries.length > 0 && (
          <div className="space-y-2 pt-1 border-t border-border/60">
            {entries.map((e) => (
              <div key={e.id} className="flex items-start gap-2" dir="auto">
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-[10px] text-muted-foreground" dir="ltr">
                    {formatWhen(e.recorded_at, locale)}
                  </p>
                  <p className="text-sm text-foreground">{e.summary || e.transcript.slice(0, 120)}</p>
                  <EntryChips entry={e} t={t} />
                </div>
                <button
                  type="button"
                  onClick={() => remove(e.id)}
                  aria-label={t('common.delete')}
                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground flex-shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function EntryChips({ entry, t }) {
  const chips = []
  if (entry.mood != null) chips.push(`${t('journey.checkin.mood')} ${entry.mood}/5`)
  if (entry.energy != null) chips.push(`${t('journey.checkin.energy')} ${entry.energy}/5`)
  for (const p of entry.people || []) chips.push(`@${p}`)
  for (const tp of entry.topics || []) chips.push(`#${tp}`)
  if (!chips.length) return null
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <span key={i} className="px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 text-[10px]" dir="auto">
          {c}
        </span>
      ))}
    </div>
  )
}

function formatWhen(iso, locale) {
  try {
    return new Date(iso).toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}
